<!-- components/qr/DownloadOptions.vue -->
<script setup>
import { ref, computed } from "vue";
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  PhotoIcon,
  CogIcon,
  CheckIcon,
} from "@heroicons/vue/24/outline";
import { useToast } from "vue-toastification";

// Props & Emits
const props = defineProps({
  qrCodeData: {
    type: String,
    required: true,
  },
  filename: {
    type: String,
    default: "qrcode",
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["download"]);

// Composables
const toast = useToast();

// Reactive state
const selectedFormat = ref("png");
const selectedSize = ref(512);
const includeBackground = ref(true);
const isDownloading = ref(false);

// Available options
const downloadFormats = [
  {
    value: "png",
    label: "PNG",
    description: "Best for web and print",
    icon: PhotoIcon,
  },
  {
    value: "jpeg",
    label: "JPEG",
    description: "Smaller file size",
    icon: PhotoIcon,
  },
  {
    value: "svg",
    label: "SVG",
    description: "Vector format, scalable",
    icon: DocumentIcon,
  },
  {
    value: "pdf",
    label: "PDF",
    description: "Print-ready document",
    icon: DocumentIcon,
  },
];

const downloadSizes = [
  { value: 256, label: "256x256", description: "Small" },
  { value: 512, label: "512x512", description: "Medium (Recommended)" },
  { value: 1024, label: "1024x1024", description: "Large" },
  { value: 2048, label: "2048x2048", description: "Extra Large" },
];

// Computed
const estimatedFileSize = computed(() => {
  const baseSize = selectedSize.value;
  let multiplier = 1;

  switch (selectedFormat.value) {
    case "png":
      multiplier = 0.8;
      break;
    case "jpeg":
      multiplier = 0.3;
      break;
    case "svg":
      multiplier = 0.1;
      break;
    case "pdf":
      multiplier = 1.2;
      break;
  }

  const sizeKB = Math.round((baseSize * baseSize * multiplier) / 1000);
  return sizeKB > 1000 ? `${(sizeKB / 1000).toFixed(1)} MB` : `${sizeKB} KB`;
});

const downloadFilename = computed(() => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
  return `${props.filename}-${timestamp}-${selectedSize.value}.${selectedFormat.value}`;
});

// Methods
const downloadQR = async () => {
  if (!props.qrCodeData) {
    toast.error("No QR code to download");
    return;
  }

  isDownloading.value = true;

  try {
    const downloadData = {
      format: selectedFormat.value,
      size: selectedSize.value,
      includeBackground: includeBackground.value,
      filename: downloadFilename.value,
      qrCodeData: props.qrCodeData,
    };

    // Emit download event to parent
    emit("download", downloadData);

    // For immediate download
    await performDownload(downloadData);

    toast.success(`QR code downloaded as ${downloadData.filename}`);
  } catch (error) {
    console.error("Download failed:", error);
    toast.error("Download failed. Please try again.");
  } finally {
    isDownloading.value = false;
  }
};

const performDownload = async (downloadData) => {
  const link = document.createElement("a");

  if (downloadData.format === "svg") {
    // Handle SVG download
    const svgData = await generateSVG(downloadData);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    link.href = URL.createObjectURL(blob);
  } else if (downloadData.format === "pdf") {
    // Handle PDF download
    const pdfData = await generatePDF(downloadData);
    link.href = pdfData;
  } else {
    // Handle raster formats (PNG, JPEG)
    const canvas = await generateCanvas(downloadData);
    link.href = canvas.toDataURL(`image/${downloadData.format}`, 0.9);
  }

  link.download = downloadData.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup
  if (link.href.startsWith("blob:")) {
    URL.revokeObjectURL(link.href);
  }
};

const generateCanvas = async (downloadData) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.onload = () => {
      canvas.width = downloadData.size;
      canvas.height = downloadData.size;

      // Add background if requested
      if (downloadData.includeBackground) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Draw QR code
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas);
    };

    img.onerror = reject;
    img.src = downloadData.qrCodeData;
  });
};

const generateSVG = async (downloadData) => {
  // Convert canvas to SVG (simplified)
  const size = downloadData.size;
  return `<svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
    <image href="${downloadData.qrCodeData}" width="${size}" height="${size}"/>
  </svg>`;
};

const generatePDF = async (downloadData) => {
  // This would typically use a library like jsPDF
  // For now, return the data URL
  return downloadData.qrCodeData;
};

const quickDownload = async (format) => {
  selectedFormat.value = format;
  await downloadQR();
};
</script>

<template>
  <div class="bg-white p-4 tablet:p-6 rounded-lg border border-gray-200 min-w-0">
    <div class="flex items-center space-x-2 mb-6">
      <ArrowDownTrayIcon class="w-5 h-5 text-blue-600" />
      <h3 class="text-lg font-semibold text-gray-800">Download Options</h3>
    </div>

    <!-- Format Selection -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-3"
        >File Format</label
      >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          v-for="format in downloadFormats"
          :key="format.value"
          role="button"
          tabindex="0"
          @click="selectedFormat = format.value"
          @keydown.enter="selectedFormat = format.value"
          @keydown.space.prevent="selectedFormat = format.value"
          class="relative p-4 min-h-[44px] border rounded-lg cursor-pointer transition-all hover:border-blue-300 flex items-center"
          :class="{
            'border-blue-500 bg-blue-50': selectedFormat === format.value,
            'border-gray-200': selectedFormat !== format.value,
          }"
        >
          <div class="flex items-start space-x-3">
            <component :is="format.icon" class="w-5 h-5 text-gray-600 mt-0.5" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2">
                <span class="font-medium text-gray-900">{{
                  format.label
                }}</span>
                <CheckIcon
                  v-if="selectedFormat === format.value"
                  class="w-4 h-4 text-blue-600"
                />
              </div>
              <p class="text-sm text-gray-500">{{ format.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Size Selection -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-3"
        >Image Size</label
      >
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button
          v-for="size in downloadSizes"
          :key="size.value"
          type="button"
          @click="selectedSize = size.value"
          class="p-3 min-h-[44px] text-center border rounded-lg transition-all hover:border-blue-300"
          :class="{
            'border-blue-500 bg-blue-50 text-blue-700':
              selectedSize === size.value,
            'border-gray-200 text-gray-700': selectedSize !== size.value,
          }"
        >
          <div class="font-medium">{{ size.label }}</div>
          <div class="text-xs opacity-75">{{ size.description }}</div>
        </button>
      </div>
    </div>

    <!-- Additional Options -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 mb-3"
        >Additional Options</label
      >
      <div class="space-y-3">
        <label class="flex items-center">
          <input
            v-model="includeBackground"
            type="checkbox"
            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span class="ml-2 text-sm text-gray-700"
            >Include white background</span
          >
        </label>
      </div>
    </div>

    <!-- Download Info -->
    <div class="mb-6 p-4 bg-gray-50 rounded-lg">
      <div class="grid grid-cols-1 tablet:grid-cols-2 gap-4 text-sm">
        <div>
          <span class="text-gray-500">Format:</span>
          <span class="ml-1 font-medium">{{
            selectedFormat.toUpperCase()
          }}</span>
        </div>
        <div>
          <span class="text-gray-500">Size:</span>
          <span class="ml-1 font-medium"
            >{{ selectedSize }}×{{ selectedSize }}</span
          >
        </div>
        <div>
          <span class="text-gray-500">Est. Size:</span>
          <span class="ml-1 font-medium">{{ estimatedFileSize }}</span>
        </div>
        <div>
          <span class="text-gray-500">Filename:</span>
          <span class="ml-1 font-medium text-xs truncate">{{
            downloadFilename
          }}</span>
        </div>
      </div>
    </div>

    <!-- Download Buttons -->
    <div class="space-y-3">
      <!-- Main Download Button -->
      <button
        type="button"
        @click="downloadQR"
        :disabled="isDownloading || !qrCodeData"
        class="w-full min-h-[44px] bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
      >
        <ArrowDownTrayIcon class="w-5 h-5 mr-2" />
        <span v-if="isDownloading">Downloading...</span>
        <span v-else>Download QR Code</span>
      </button>

      <!-- Quick Download Options -->
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          @click="quickDownload('png')"
          :disabled="isDownloading"
          class="min-h-[44px] px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors"
        >
          Quick PNG
        </button>
        <button
          type="button"
          @click="quickDownload('jpeg')"
          :disabled="isDownloading"
          class="min-h-[44px] px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors"
        >
          Quick JPEG
        </button>
      </div>
    </div>

    <!-- Tips -->
    <div class="mt-4 p-3 bg-blue-50 rounded-lg">
      <h4 class="text-sm font-medium text-blue-800 mb-1">Download Tips:</h4>
      <ul class="text-sm text-blue-700 space-y-1">
        <li>• PNG format preserves quality best</li>
        <li>• Larger sizes print better but create bigger files</li>
        <li>• SVG format is scalable for any size</li>
        <li>• Include background for better printing</li>
      </ul>
    </div>
  </div>
</template>